import { useCallback, useRef, useState } from 'react'
import {
    AsgnFullTypes,
    AsgnStateActions,
    AsgnStateType,
    Assignation,
} from './types'
import {
    DEFAULT_ASGN_STATE,
    DEFAULT_AUTH_STATE,
    FSM_STATE_TYPE,
} from './defaultState'
import { info } from '../services/sentryLogger'
import { rounds2str } from '../utils/stm'
import { prepareRounds } from './helpers/stms'
import { FullParameters, PartialParameters } from '../parameters/types'
import { AnswserScoreType } from '../services/types'
import { SessionData, SessionUpdaterFn } from './session/types'
import { langIndex2AppLang } from '../i18n/useTranslation'
import { saveLocalSession } from './session/helpers'
import _ from 'lodash'
import { ALL_SLUGS } from '../parameters/clients'
import { diffSeconds } from '../utils'

export const useAsgnState = () => {
    // FIXME redundant with rounds
    const asgnsLeft2Rank = useRef<Assignation[]>([])
    const asgnsRanked = useRef<Assignation[]>([])
    const [rounds, setRounds] = useState<Assignation[][]>([])

    // current step FIXME remove
    const [asgnsForRound, setAsgnsForRound] = useState<Assignation[]>([])
    // For binary & n-ary screens, to compare with current item
    const [items2compare, setItems2compare] = useState<Assignation[]>([])
    // For d&d screens (profile & end of round)
    const [items2rank, setItems2rank] = useState<Assignation[]>([])
    // Choice if submit step is required
    const [index2submit, setIndex2submit] = useState<number>(-1)

    // 360 scanning
    const [scannedItems, setScannedItems] = useState<AnswserScoreType[]>([])
    const [allStms, setAllStms] =
        useState<Record<Assignation['preferredType360'], Assignation[]>>() //FIXME

    // reporting
    const [sessionData, setSessionData] = useState<SessionData>({
        asgnsRound2Rank: [],
        round1: [],
        round1Result: [],
        round2: [],
        round2Result: [],
        round3: [],
        round3Result: [],
        bucketRound: [],
        finalResult: [],
        timeSec: 0,
        restorable: {} as SessionData['restorable'], // FIXME
    })

    const setAndSaveSessionData = useCallback(
        (sessionUpdater: SessionUpdaterFn) => {
            setSessionData((sd) => {
                const updated = sessionUpdater(sd);
                // META
                updated.restorable.appVersion = process.env.REACT_APP_VERSION; //FIXME only once
                updated.restorable.durationAtSave =
                    sd.restorable.durationAtSave +
                    diffSeconds(new Date(sd.restorable.savedDateStr));
                updated.restorable.savedDateStr = new Date().toISOString();
                // FIXME
                info(
                    'Saving local session (setAndSaveSessionData) ...',
                    JSON.stringify(updated.restorable, null, 2)
                )
                saveLocalSession(updated)
                return updated
            })
        },
        [saveLocalSession]
    )

    const [state, setState] = useState<AsgnStateType>(DEFAULT_ASGN_STATE)

    const resetAsgnState: AsgnStateActions['resetAsgnState'] = useCallback(
        (
            fsmState: FSM_STATE_TYPE = DEFAULT_ASGN_STATE.fsmState,
            newParams?: PartialParameters,
            loginState?: typeof DEFAULT_AUTH_STATE
        ) => {
            const params: FullParameters = {
                brandParams: _.defaults(
                    _.cloneDeep(newParams?.brandParams),
                    DEFAULT_ASGN_STATE.params.brandParams
                ),
                assessmentParams: _.defaults(
                    _.cloneDeep(newParams?.assessmentParams),
                    DEFAULT_ASGN_STATE.params.assessmentParams
                ),
            }
            console.debug('resetAsgnState()', { newParams, params })
            const { initialRounds, asgnsRound2Rank, allStms, scannedItems } =
                prepareRounds(params)
            info('resetAsgnState()', 'initialRounds', rounds2str(initialRounds))
            setRounds(initialRounds)
            const startedAt = new Date()
            setState((s) => ({
                ...DEFAULT_ASGN_STATE,
                ...s,
                startedAt, //FIXME validate if correctlt rehydrated
                fsmState,
                params,
            }))
            setSessionData((d) => ({
                ...d,
                asgnsRound2Rank,
                restorable: {
                    ...d.restorable,
                    // INIT
                    startedAtStr: startedAt.toISOString(),
                    savedDateStr: startedAt.toISOString(),
                    durationAtSave: 0,
                    userId: loginState?.userId as string,
                    // 
                    slug: loginState?.slug as ALL_SLUGS,
                    source: loginState?.source as string,
                    from: loginState?.from as typeof DEFAULT_AUTH_STATE.from,
                    lang: langIndex2AppLang(params.assessmentParams['langIndex']),
                    //
                    currentRoundIndex: 0,
                    fsmState,
                    savedRounds: initialRounds.map((round) => ({
                        originalShuffledRound: round.map(
                            (a: any) => a.valueKey
                        ),
                        partiallyRankedRound: [],
                        currentStmIndex: 0,
                    })),
                },
            }))
            // FIXME bug here?
            // wrong : currentAsgns, item2sort
            // right : assngLeft2rank, round
            const currentAsgns = initialRounds[state.currentRound - 1]
            setAsgnsForRound([...currentAsgns]) // wrong?
            asgnsLeft2Rank.current = [...currentAsgns] //right
            asgnsRanked.current = []
            setScannedItems([...scannedItems])
            setAllStms({ ...allStms })
            console.debug('resetAsgnState() done', allStms)
        },
        []
    )

    let output: AsgnFullTypes = {
        allStms,
        scannedItems,
        setScannedItems,
        resetAsgnState,
        asgnsForRound,
        setAsgnsForRound,
        asgnsLeft2Rank,
        asgnsRanked,
        sessionData,
        setSessionData,
        setAndSaveSessionData,
        items2compare,
        setItems2compare,
        items2rank,
        setItems2rank,
        index2submit,
        setIndex2submit,
        rounds,
        setRounds,
        setState,
        // FIXME
        state,
    }
    return output
}
